import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Flag,
  RefreshCw,
  Calendar,
  Clock,
  AlertCircle,
  User
} from 'lucide-react';
import axios from 'axios';
import zIndex from '@mui/material/styles/zIndex';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function PriorityTasksDisplay() {
  const [priorityTasks, setPriorityTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);

  // Priority and status mappings
  const priorityMap = {
    1: { label: 'Highest', color: 'error' },
    2: { label: 'High', color: 'warning' },
    3: { label: 'Medium', color: 'info' },
    4: { label: 'Low', color: 'primary' },
    5: { label: 'Lowest', color: 'success' }
  };

  const statusMap = {
    1: { label: 'To Do', color: 'default' },
    2: { label: 'In Progress', color: 'primary' },
    3: { label: 'Done', color: 'success' },
    4: { label: 'Blocked', color: 'error' }
  };

  const issueTypeMap = {
    1: 'Bug',
    2: 'Epic',
    3: 'Story',
    4: 'Task',
    
  };

  // Fetch current user to get employee_id
  const fetchCurrentUser = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });

      if (!response.data || !response.data.employee_id) {
        throw new Error('Employee ID not found in user data');
      }

      setEmployeeId(response.data.employee_id);
      return response.data.employee_id;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      return null;
    }
  };

  // Fetch priority tasks for the logged-in employee
  const fetchPriorityTasks = async (empId = null) => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(true);
      setError(null);

      const currentEmployeeId = empId || employeeId;

      if (!currentEmployeeId) {
        throw new Error('Employee ID not available');
      }

      const response = await fetch(`${API_URL}/api/priority-tasks?employee_id=${currentEmployeeId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          "Authorization": `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Sort tasks by priority (1 = highest priority)
      const sortedTasks = data.sort((a, b) => a.priority - b.priority);
      setPriorityTasks(sortedTasks);

    } catch (err) {
      //console.error('Error fetching priority tasks:', err);
      setError(err.message || 'Failed to fetch priority tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeComponent = async () => {
      const empId = await fetchCurrentUser();
      if (empId) {
        await fetchPriorityTasks(empId);
      }
      
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.company_id) {
            const res = await axios.get(`${API_URL}/api/cmp/${payload.company_id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data && res.data.data) {
              setCompanyConfig(res.data.data);
            }
          }
        } catch (e) {}
      }
    };

    initializeComponent();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    if (companyConfig?.timezone) {
      options.timeZone = companyConfig.timezone;
    }
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'success';
    if (progress >= 50) return 'info';
    if (progress >= 25) return 'warning';
    return 'error';
  };

  const handleRefresh = () => {
    if (employeeId) {
      fetchPriorityTasks();
    } else {
      const reinitialize = async () => {
        const empId = await fetchCurrentUser();
        if (empId) {
          await fetchPriorityTasks(empId);
        }
      };
      reinitialize();
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: 450 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress size={48} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '450px', overflow: 'auto', border: '1px solid', borderColor: 'grey.300', boxShadow: 2 }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          px: 3,
          py: 2,
          borderBottom: '1px solid #eee',
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Flag size={24} color="#f44336" />
            <Typography variant="h6" fontWeight="bold">
              My Priority Tasks
            </Typography>
          </Box>
          {/* <Tooltip title="Refresh tasks">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshCw size={20} />
            </IconButton>
          </Tooltip> */}
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tasks List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {priorityTasks.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
              <Flag size={48} color="#bdbdbd" />
              <Typography variant="body1" color="text.secondary" mt={2}>
                No priority tasks assigned to you.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {priorityTasks.map((task) => (
                <Card
                  key={task.id}
                  variant="outlined"
                  sx={{
                    borderLeft: 4,
                    borderLeftColor: task.priority === 1 ? 'error.main' :
                                     task.priority === 2 ? 'warning.main' :
                                     task.priority === 3 ? 'info.main' :
                                     task.priority === 4 ? 'primary.main' : 'success.main',
                    '&:hover': {
                      boxShadow: 2
                    }
                  }}
                >
                  <CardContent sx={{ pb: 2 }}>
                    {/* Task Header */}
                    <Box mb={2}>
                      {/* First line: key and issue name */}
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight="bold">
                            {task.key}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            •
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {issueTypeMap[task.issueType]}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Second line: chips */}
                      <Box mt={0.3} display="flex" flexDirection="row" gap={1} alignItems="center" ml={10}>
                        <Chip
                          label={priorityMap[task.priority]?.label || "Unknown"}
                          color={priorityMap[task.priority]?.color || "default"}
                          size="small"
                        />
                        <Chip
                          label={statusMap[task.status]?.label || "Unknown"}
                          color={statusMap[task.status]?.color || "default"}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </Box>

                    {/* Task Description */}
                    {task.description && (
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {task.description.length > 100
                          ? `${task.description.substring(0, 100)}...`
                          : task.description}
                      </Typography>
                    )}

                    {/* Progress Bar */}
                    {/* <Box mb={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          Progress
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {task.progress || 0}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={task.progress || 0}
                        color={getProgressColor(task.progress || 0)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box> */}

                    {/* Task Details */}
                    {/* <Grid container spacing={2} alignItems="center">
                      <Grid item xs={8}>
                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                          {task.storyPoints && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" fontWeight="medium">
                                SP:
                              </Typography>
                              <Typography variant="body2">
                                {task.storyPoints}
                              </Typography>
                            </Box>
                          )}
                          {task.timeEstimate && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Clock size={16} />
                              <Typography variant="body2">
                                {task.timeEstimate}
                              </Typography>
                            </Box>
                          )}
                          {task.startDate && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Calendar size={16} />
                              <Typography variant="body2">
                                {formatDate(task.startDate)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary" textAlign="right">
                          Due: {formatDate(task.endDate)}
                        </Typography>
                      </Grid>
                    </Grid> */}

                  
                    {/* {task.labels && task.labels.length > 0 && (
                      <Box mt={2}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {task.labels.map((label, index) => (
                            <Chip
                              key={index}
                              label={label}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )} */}

                   
                    {/* {task.isOverdue && (
                      <Box mt={2} display="flex" alignItems="center" gap={0.5}>
                        <AlertCircle size={14} color="#f44336" />
                        <Typography variant="caption" color="error">
                          Task is overdue
                        </Typography>
                      </Box>
                    )} */}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}